import React, { createContext, useContext, useReducer, ReactNode } from 'react';

export interface Alert {
  id: string;
  type: 'missing' | 'location_change' | 'system';
  message: string;
  invoiceId: number;
  poId: string;
  createdAt: string;
  resolved: boolean;
  severity: 'low' | 'medium' | 'high' | 'critical';
  section: string; // Which section the alert is related to
  data?: any; // Additional data for the alert
}

interface AlertState {
  alerts: Alert[];
  unreadCount: number;
}

type AlertAction = 
  | { type: 'ADD_ALERT'; payload: Alert }
  | { type: 'RESOLVE_ALERT'; payload: string }
  | { type: 'RESOLVE_ALL_ALERTS' }
  | { type: 'DELETE_ALERT'; payload: string }
  | { type: 'LOAD_ALERTS'; payload: Alert[] }
  | { type: 'MARK_ALL_READ' };

const initialState: AlertState = {
  alerts: [],
  unreadCount: 0,
};

function alertReducer(state: AlertState, action: AlertAction): AlertState {
  switch (action.type) {
    case 'ADD_ALERT':
      const newAlert = { ...action.payload, resolved: false };
      return {
        ...state,
        alerts: [newAlert, ...state.alerts],
        unreadCount: state.unreadCount + 1,
      };
    
    case 'RESOLVE_ALERT':
      return {
        ...state,
        alerts: state.alerts.map(alert =>
          alert.id === action.payload ? { ...alert, resolved: true } : alert
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    
    case 'RESOLVE_ALL_ALERTS':
      return {
        ...state,
        alerts: state.alerts.map(alert => ({ ...alert, resolved: true })),
        unreadCount: 0,
      };
    
    case 'DELETE_ALERT':
      const alertToDelete = state.alerts.find(alert => alert.id === action.payload);
      return {
        ...state,
        alerts: state.alerts.filter(alert => alert.id !== action.payload),
        unreadCount: alertToDelete?.resolved ? state.unreadCount : Math.max(0, state.unreadCount - 1),
      };
    
    case 'LOAD_ALERTS':
      return {
        ...state,
        alerts: action.payload,
        unreadCount: action.payload.filter(alert => !alert.resolved).length,
      };
    
    case 'MARK_ALL_READ':
      return {
        ...state,
        unreadCount: 0,
      };
    
    default:
      return state;
  }
}

interface AlertContextType {
  state: AlertState;
  addAlert: (alert: Omit<Alert, 'id' | 'createdAt' | 'resolved'>) => void;
  resolveAlert: (alertId: string) => void;
  resolveAllAlerts: () => void;
  deleteAlert: (alertId: string) => void;
  markAllRead: () => void;
  getAlertsBySection: (section: string) => Alert[];
  getAlertsByType: (type: Alert['type']) => Alert[];
  getUnresolvedAlerts: () => Alert[];
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export function AlertProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(alertReducer, initialState);

  // Load alerts from localStorage on mount
  React.useEffect(() => {
    const savedAlerts = localStorage.getItem('PROCUREMENT_ALERTS');
    if (savedAlerts) {
      try {
        const alerts = JSON.parse(savedAlerts);
        dispatch({ type: 'LOAD_ALERTS', payload: alerts });
      } catch (error) {
        console.error('Failed to load alerts from localStorage:', error);
      }
    }
  }, []);

  // Save alerts to localStorage whenever they change
  React.useEffect(() => {
    localStorage.setItem('PROCUREMENT_ALERTS', JSON.stringify(state.alerts));
  }, [state.alerts]);

  const addAlert = (alertData: Omit<Alert, 'id' | 'createdAt' | 'resolved'>) => {
    const alert: Alert = {
      ...alertData,
      id: `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      resolved: false,
    };
    
    dispatch({ type: 'ADD_ALERT', payload: alert });
  };

  const resolveAlert = (alertId: string) => {
    dispatch({ type: 'RESOLVE_ALERT', payload: alertId });
  };

  const resolveAllAlerts = () => {
    dispatch({ type: 'RESOLVE_ALL_ALERTS' });
  };

  const deleteAlert = (alertId: string) => {
    dispatch({ type: 'DELETE_ALERT', payload: alertId });
  };

  const markAllRead = () => {
    dispatch({ type: 'MARK_ALL_READ' });
  };

  const getAlertsBySection = (section: string) => {
    return state.alerts.filter(alert => alert.section === section);
  };

  const getAlertsByType = (type: Alert['type']) => {
    return state.alerts.filter(alert => alert.type === type);
  };

  const getUnresolvedAlerts = () => {
    return state.alerts.filter(alert => !alert.resolved);
  };

  const value: AlertContextType = {
    state,
    addAlert,
    resolveAlert,
    resolveAllAlerts,
    deleteAlert,
    markAllRead,
    getAlertsBySection,
    getAlertsByType,
    getUnresolvedAlerts,
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
}

export function useAlerts() {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error('useAlerts must be used within an AlertProvider');
  }
  return context;
}




